/**
 * PlanPicker — embedded-app UI for Shopify Billing subscription selection.
 *
 * Mounted by App.tsx when:
 *   1. Shop is installed (we have a session)
 *   2. No active Shopify subscription exists for this shop, AND
 *   3. The user hasn't been migrated through the OTP/legacy linking flow yet.
 *
 * Flow:
 *   - Loads plans from /api/billing/plans
 *   - On "Subscribe" click → POST /api/billing/create-subscription
 *   - Top-level redirect to confirmationUrl (App Bridge nav action)
 *   - After merchant approves on Shopify → returns to /?charge_id=…
 *     App.tsx detects this and polls /api/billing/status until ACTIVE
 *
 * Why a separate component (not inline in App.tsx): keeps the existing OTP/
 * connection state machine isolated. Once Shopify Billing is approved, the
 * OTP flow remains as the linking mechanism for already-paid Dashbal users.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Layout,
  Page,
  Text,
  Button,
  Spinner,
  BlockStack,
  InlineStack,
  Badge,
  Box,
  Banner,
  List,
  Divider,
} from "@shopify/polaris";

interface Plan {
  key: string;
  displayName: string;
  isFree: boolean;
  amount: number;
  currency: string;
  trialDays: number;
  maxShopifyStores: number;
  features?: string[];
}

interface PlanPickerProps {
  shop: string;
  shopCountry?: string;
  getAuthHeaders: () => Promise<Record<string, string>>;
  /** Called once the merchant has been redirected to Shopify approval. */
  onCheckoutInitiated?: (plan: Plan) => void;
  /** Called when the merchant opts to continue on the free tier (no Shopify charge). */
  onContinueFree?: (plan: Plan) => void;
  /**
   * Re-fetches /api/billing/status and returns the fresh payload. Called
   * before ANY plan action: this picker can sit open while billing changes in
   * another tab (e.g. the merchant subscribes on dashbal.com), and a stale
   * snapshot must never lead to a second charge or a silent downgrade.
   */
  recheckBillingStatus?: () => Promise<{ hasActiveWebsiteSubscription?: boolean } | null>;
  /**
   * Called when the freshness check (or a 409 from the backend) shows the
   * account is already billed via the website — the host swaps this picker
   * for a "you're all set" confirmation.
   */
  onWebsiteBillingDetected?: () => void;
  /**
   * If set, the picker is being re-mounted from a connected state (plan change)
   * rather than a fresh install. We surface the current plan as a badge and
   * show a back button via onCancel.
   */
  currentPlanKey?: string | null;
  /**
   * The plan the merchant was last on when they currently have NO active
   * subscription (e.g. after an uninstall → grace_period). Surfaced as a
   * "Your previous plan" badge + "Resubscribe" CTA so a reinstalling merchant
   * can pick up where they left off in one tap. Unlike currentPlanKey it does
   * NOT disable the plan's button — they need to re-approve the charge.
   */
  previousPlanKey?: string | null;
  /** If provided, renders a back button that returns the merchant to the app. */
  onCancel?: () => void;
}

export default function PlanPicker({
  shop,
  shopCountry = "US",
  getAuthHeaders,
  onCheckoutInitiated,
  onContinueFree,
  recheckBillingStatus,
  onWebsiteBillingDetected,
  currentPlanKey,
  previousPlanKey,
  onCancel,
}: PlanPickerProps) {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submittingKey, setSubmittingKey] = useState<string | null>(null);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `/api/billing/plans?shopCountry=${encodeURIComponent(shopCountry)}`,
        { headers },
      );
      const body = await res.json();
      if (!res.ok || !body.success) {
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setPlans(body.data || []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load plans";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, shopCountry]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleSubscribe = useCallback(
    async (plan: Plan) => {
      setSubmittingKey(plan.key);
      try {
        // Freshness gate: billing can change in another tab while this picker
        // is open (the merchant may have subscribed on dashbal.com). Re-check
        // before charging or provisioning anything — a website-billed account
        // gets the "you're all set" state, never a second checkout.
        if (recheckBillingStatus) {
          const fresh = await recheckBillingStatus();
          if (fresh?.hasActiveWebsiteSubscription) {
            setSubmittingKey(null);
            onWebsiteBillingDetected?.();
            return;
          }
        }

        if (plan.isFree) {
          // Free plan: no Shopify subscription needed. Hand control back to
          // App.tsx so the merchant exits the picker and lands in the app.
          setSubmittingKey(null);
          onContinueFree?.(plan);
          return;
        }

        const headers = await getAuthHeaders();
        headers["Content-Type"] = "application/json";
        // Preserve the embedded-app context in the return URL so that when
        // Shopify bounces the merchant back top-level after billing approval,
        // App Bridge can re-initialize (needs `host`) and the SDK's
        // validateAuthenticatedSession can resolve a session (needs `shop`).
        // Without these the post-billing fetch hits an SDK re-auth chain that
        // 500s with plaintext "No shop provided".
        const currentParams = new URLSearchParams(window.location.search);
        const host =
          currentParams.get("host") ||
          (window.shopify as { config?: { host?: string } })?.config?.host ||
          "";
        const returnUrl =
          `${window.location.origin}/?billing_return=1` +
          `&plan=${encodeURIComponent(plan.key)}` +
          `&shop=${encodeURIComponent(shop)}` +
          (host ? `&host=${encodeURIComponent(host)}&embedded=1` : "");

        const res = await fetch("/api/billing/create-subscription", {
          method: "POST",
          headers,
          body: JSON.stringify({
            planKey: plan.key,
            returnUrl,
            shopCountry,
          }),
        });
        const body = await res.json();
        if (!res.ok || !body.success) {
          // Server-side invariant guards (the authoritative backstop for the
          // freshness gate above) come back as 409 with a stable code.
          if (body.code === "BILLING_MANAGED_BY_WEBSITE") {
            setSubmittingKey(null);
            await recheckBillingStatus?.();
            onWebsiteBillingDetected?.();
            return;
          }
          if (body.code === "ACTIVE_SUBSCRIPTION_ON_OTHER_STORE") {
            throw new Error(t("billing.activeOnOtherStore"));
          }
          throw new Error(body.error || `HTTP ${res.status}`);
        }
        const confirmationUrl = body.data?.confirmationUrl;
        if (!confirmationUrl) {
          throw new Error("No confirmation URL returned");
        }
        onCheckoutInitiated?.(plan);

        // Top-level redirect — required for Shopify Billing approval page.
        // App Bridge's `open` won't work for off-iframe redirects, so we use
        // window.top.location which the embedded iframe is allowed to do.
        if (window.top) {
          window.top.location.href = confirmationUrl;
        } else {
          window.location.href = confirmationUrl;
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Subscribe failed";
        setError(msg);
        setSubmittingKey(null);
      }
    },
    [
      getAuthHeaders,
      onCheckoutInitiated,
      onContinueFree,
      recheckBillingStatus,
      onWebsiteBillingDetected,
      shop,
      shopCountry,
      t,
    ],
  );

  if (loading) {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack inlineAlign="center" gap="400">
                <Spinner accessibilityLabel={t("billing.loading")} />
                <Text as="p">{t("billing.loading")}</Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={t("billing.pickerTitle")}
      backAction={onCancel ? { content: t("billing.backToApp"), onAction: onCancel } : undefined}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}

        {plans.map((plan) => (
          <Layout.Section key={plan.key}>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="h3" variant="headingLg">
                        {plan.displayName}
                      </Text>
                      {currentPlanKey && currentPlanKey === plan.key && (
                        <Badge tone="success">
                          {t("billing.currentPlanBadge", "Current plan")}
                        </Badge>
                      )}
                      {!currentPlanKey && previousPlanKey && previousPlanKey === plan.key && (
                        <Badge tone="attention">
                          {t("billing.previousPlanBadge", "Your previous plan")}
                        </Badge>
                      )}
                      {plan.isFree ? (
                        <Badge tone="info">
                          {t("billing.freeBadge", "No payment required")}
                        </Badge>
                      ) : (
                        plan.trialDays > 0 && (
                          <Badge tone="success">
                            {t("billing.trialBadge", { days: plan.trialDays })}
                          </Badge>
                        )
                      )}
                    </InlineStack>
                    <Text as="p" variant="headingMd">
                      {plan.isFree
                        ? t("billing.freePrice", "Free forever")
                        : `${plan.currency} ${plan.amount.toFixed(2)} / ${t("billing.month")}`}
                    </Text>
                    <Text as="p" tone="subdued">
                      {t("billing.includedStores", {
                        count: plan.maxShopifyStores,
                      })}
                    </Text>
                  </BlockStack>
                  {currentPlanKey && currentPlanKey === plan.key ? (
                    <Button variant="secondary" size="large" disabled>
                      {t("billing.alreadyOnPlan", "Already on this plan")}
                    </Button>
                  ) : plan.isFree ? (
                    <Button
                      variant="secondary"
                      size="large"
                      loading={submittingKey === plan.key}
                      disabled={!!submittingKey && submittingKey !== plan.key}
                      onClick={() => handleSubscribe(plan)}
                    >
                      {t("billing.continueFree", "Continue with Free")}
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="large"
                      loading={submittingKey === plan.key}
                      disabled={!!submittingKey && submittingKey !== plan.key}
                      onClick={() => handleSubscribe(plan)}
                    >
                      {previousPlanKey && previousPlanKey === plan.key
                        ? t("billing.resubscribe", "Resubscribe")
                        : t("billing.subscribe")}
                    </Button>
                  )}
                </InlineStack>
                {plan.features && plan.features.length > 0 && (
                  <>
                    <Divider />
                    <List type="bullet">
                      {plan.features.map((f, i) => (
                        <List.Item key={i}>{f}</List.Item>
                      ))}
                    </List>
                  </>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        ))}

        <Layout.Section>
          <Box paddingBlockStart="400">
            <Text as="p" tone="subdued" alignment="center">
              {t("billing.legalCharge")}
            </Text>
          </Box>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
